import React, { useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { GlobalStyles } from './styles/globalStyles';
import Navigation from './components/Navigation';
import { theme } from './config/theme';
import HomePage from './pages/HomePage';
import BookClassPage from './pages/BookClassPage';
import GroupClassesPage from './pages/GroupClassesPage';
import PrivateClassesPage from './pages/PrivateClassesPage';
import EventsPage from './pages/EventsPage';
import PurchasePage from './pages/PurchasePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import InstructorPage from './pages/InstructorPage';
import JoinOurTeamPage from './pages/JoinOurTeamPage';
import LocationPage from './pages/LocationPage';
import VirtualTourPage from './pages/VirtualTourPage';
import MovementGalleryPage from './pages/MovementGalleryPage';
import CheckoutPage from './junto-frontend/components/pages/CheckoutPage';
import CheckoutSuccessPage from './junto-frontend/components/pages/CheckoutSuccessPage';
import { BackButtonProvider } from './contexts/BackButtonContext';
import { AuthProvider } from './junto-frontend/contexts/ClerkAuthContext';
import { PopupProvider, usePopup } from './junto-frontend/contexts/PopupContext';
import { FeatureFlagProvider } from './junto-frontend/contexts/FeatureFlags';
import { CartProvider } from './junto-frontend/contexts/CartContext';
import { CustomAccountPopup } from './junto-frontend/components/AuthSystem';

const AppWrapper = styled.div`
  min-height: 100vh;
  position: relative;
`;

// Component to handle scroll-to-top on route changes
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Only scroll to top on pathname changes (not hash changes)
    // and only when there's no hash in the new route
    if (!hash) {
      // Use requestAnimationFrame to avoid conflicts with other scroll operations
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
    // If there's a hash, individual page components handle anchor scrolling
  }, [pathname, hash]);

  return null;
}

// Component to render popups that need to be at the app level
function AppPopups() {
  const { accountPopupOpen, setAccountPopupOpen } = usePopup();

  return (
    <>
      {/* AuthPopup removed - using Clerk's hosted authentication */}
      
      <CustomAccountPopup 
        isOpen={accountPopupOpen}
        onClose={() => setAccountPopupOpen(false)}
      />
    </>
  );
}

// Get Clerk publishable key from environment
// Build timestamp: ${new Date().toISOString()}
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  console.error('Missing REACT_APP_CLERK_PUBLISHABLE_KEY environment variable');
}

function App() {
  useEffect(() => {
    // Dynamically set the theme-color meta tag based on theme
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme.colors.accent.main);
    }
  }, []);

  // Don't render ClerkProvider if the key is missing
  if (!clerkPubKey) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          fontFamily: 'Arial, sans-serif',
          color: '#d32f2f',
          backgroundColor: '#ffebee',
          border: '1px solid #f8bbd9',
          borderRadius: '4px',
          margin: '20px'
        }}>
          <h2>Configuration Error</h2>
          <p>Missing Clerk publishable key. Please check your environment variables.</p>
          <p>Expected: <code>REACT_APP_CLERK_PUBLISHABLE_KEY</code></p>
        </div>
      </ThemeProvider>
    );
  }

  // Customize Clerk's appearance to match The Sol Room branding
  const clerkAppearance = {
    baseTheme: undefined, // Use default light theme
    elements: {
      formButtonPrimary: {
        backgroundColor: theme.colors.primary.background,
        color: theme.colors.primary.buttons,
        border: `1px solid ${theme.colors.primary.buttons}`,
        '&:hover': {
          backgroundColor: theme.colors.primary.buttons,
          color: theme.colors.primary.background,
        },
      },
      card: {
        boxShadow: 'none',
        border: `1px solid ${theme.colors.accent.bright}`,
      },
      headerTitle: {
        fontFamily: theme.typography.primary.fontFamily,
        color: theme.colors.primary.text,
      },
      headerSubtitle: {
        fontFamily: theme.typography.secondary.fontFamily,
        color: theme.colors.primary.text,
      },
      socialButtonsBlockButton: {
        border: `1px solid ${theme.colors.primary.buttons}`,
        color: theme.colors.primary.buttons,
        backgroundColor: theme.colors.primary.background,
        '&:hover': {
          backgroundColor: theme.colors.primary.buttons,
          color: theme.colors.primary.background,
          borderColor: theme.colors.primary.buttons,
        },
      },
      formFieldLabel: {
        color: theme.colors.primary.text,
        fontFamily: theme.typography.secondary.fontFamily,
      },
      formFieldInput: {
        borderColor: theme.colors.accent.bright,
        '&:focus': {
          borderColor: theme.colors.accent.main,
        },
      },
      footerActionLink: {
        color: theme.colors.primary.buttons,
        '&:hover': {
          color: '#413a38',
        },
      },
      // Ensure all buttons have good contrast
      formButtonReset: {
        color: theme.colors.primary.buttons,
        '&:hover': {
          color: '#413a38',
        },
      },
      alternativeMethodsBlockButton: {
        border: `1px solid ${theme.colors.primary.buttons}`,
        color: theme.colors.primary.buttons,
        backgroundColor: theme.colors.primary.background,
        '&:hover': {
          backgroundColor: theme.colors.primary.buttons,
          color: theme.colors.primary.background,
        },
      },
      closeButton: {
        color: theme.colors.primary.text,
        '&:hover': {
          color: theme.colors.primary.buttons,
        },
      },
    },
    variables: {
      colorPrimary: theme.colors.primary.buttons,
      colorText: theme.colors.primary.text,
      colorTextSecondary: theme.colors.primary.text,
      colorBackground: theme.colors.primary.background,
      colorInputBackground: theme.colors.primary.background,
      colorInputText: theme.colors.primary.text,
      fontFamily: theme.typography.secondary.fontFamily,
      fontFamilyButtons: theme.typography.button.fontFamily,
      borderRadius: '8px',
    },
  };

  const clerkLocalization = {
    signIn: {
      start: {
        title: 'Welcome Back!',
        subtitle: 'Sign in to continue to The Sol Room',
      },
    },
    signUp: {
      start: {
        title: 'Join The Sol Room',
        subtitle: 'Create your account to book classes',
      },
    },
  };

  return (
    <ClerkProvider 
      publishableKey={clerkPubKey}
      appearance={clerkAppearance}
      localization={clerkLocalization}
    >
      <ThemeProvider theme={theme}>
      <FeatureFlagProvider initialFlags={{ useJuntoFrontendCompletely: true }}>
        <AuthProvider>
          <CartProvider>
            <PopupProvider>
              <BackButtonProvider>
                <Router>
                <GlobalStyles />
                <ScrollToTop />
                <AppWrapper>
                  <Navigation />
                  <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/book" element={<BookClassPage />} />
                  <Route path="/book/group" element={<GroupClassesPage />} />
                  <Route path="/book/private" element={<PrivateClassesPage />} />
                  <Route path="/book/events" element={<EventsPage />} />
                  <Route path="/purchase" element={<PurchasePage />} />
                  <Route path="/location" element={<LocationPage />} />
                  <Route path="/instructor/:slug" element={<InstructorPage />} />
                  <Route path="/join-our-team" element={<JoinOurTeamPage />} />
                  <Route path="/virtual-tour" element={<VirtualTourPage />} />
                  <Route path="/movement/:slug" element={<MovementGalleryPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                </Routes>
              </AppWrapper>
              <AppPopups />
                </Router>
              </BackButtonProvider>
            </PopupProvider>
          </CartProvider>
        </AuthProvider>
      </FeatureFlagProvider>
      </ThemeProvider>
    </ClerkProvider>
  );
}

export default App;